import { useState } from 'react';
import { LeaveDurationTypeEnum, LeaveRequestStatus, LeaveRequestType } from '../../../commonTypes/leaveRequest.types';
import { IMenuItem, Modal } from '../../../components/Common';
import RejectLeaveRequest from '../../../components/modalContent/RejectLeaveRequest';
import {
    useHttpApproveLeaveRequest,
    useHttpApproveMedicalLeaveRequest,
    useHttpGetLeaveRequestsFromOrganizationOMX,
    useHttpGetMedicalLeaveRequestsFromOrganization,
    useHttpRejectLeaveRequest,
    useHttpRejectMedicalLeaveRequest,
    useOrganizationId,
} from '../../../hooks';
import { ILeaveRequestSearchParams } from '../../../hooks/user/leaveRequestApi/useHttpGetLeaveRequestsFromOrganizationOMX';
import RequestList from '../../../components/RequestList';
import { MessageBox } from '../../../components/Common';
import { useTranslation } from '../../../hooks/useTranslation';
import { IMedicalLeaveRequestSearchParams } from '../../../hooks/acs/acsApi/useHttpGetMedicalLeaveRequestsFromOrganization';
import { LeaveRequestUserInfoIncluded, MedicalLeaveRequestUserInfoIncluded } from '../../../commonTypes/acs';

export const OwnerLeaveRequest = () => {
    const { t } = useTranslation();
    const organizationId = useOrganizationId();
    const [perPage, setPerPage] = useState<number>(10);

    const filterItems: IMenuItem[] = [
        { value: 'ALL', label: `${t('leaveRequest.all')}` },
        { value: LeaveRequestStatus.APPROVED, label: `${t('leaveRequest.approved')}` },
        { value: LeaveRequestStatus.PENDING, label: `${t('leaveRequest.pending')}` },
        { value: LeaveRequestStatus.REJECTED, label: `${t('leaveRequest.rejected')}` },
    ];
    const [selectedStatus, setSelectedStatus] = useState<IMenuItem>({
        value: LeaveRequestStatus.PENDING,
        label: `${t('leaveRequest.pending')}`,
    });

    const [selectedStatusMedical, setSelectedStatusMedical] = useState<IMenuItem>({
        value: LeaveRequestStatus.PENDING,
        label: `${t('leaveRequest.pending')}`,
    });

    const updateLeaveRequestApproved = (id: string) => {
        if (selectedStatus.value !== LeaveRequestStatus.APPROVED && selectedStatus.value !== 'ALL') {
            setLeaveRequests(prev => prev.filter(item => item.id !== id));
            return;
        }
        setLeaveRequests(prev =>
            prev.map(leaveRequest => {
                if (leaveRequest.id === id) leaveRequest.status = LeaveRequestStatus.APPROVED;
                return leaveRequest;
            })
        );
    };

    const updateLeaveRequestRejected = (id: string, rejectReason?: string) => {
        if (selectedStatus.value !== LeaveRequestStatus.REJECTED && selectedStatus.value !== 'ALL') {
            setLeaveRequests(prev => prev.filter(item => item.id !== id));
            return;
        }
        setLeaveRequests(prev =>
            prev.map(leaveRequest => {
                if (leaveRequest.id === id) {
                    leaveRequest.status = LeaveRequestStatus.REJECTED;
                    leaveRequest.rejectReason = rejectReason;
                }
                return leaveRequest;
            })
        );
    };

    const updateMedicalLeaveRequestApproved = (id: string) => {
        if (selectedStatus.value !== LeaveRequestStatus.APPROVED && selectedStatus.value !== 'ALL') {
            setMedicalLeaveRequests(prev => prev.filter(item => item.id !== id));
            return;
        }
        setMedicalLeaveRequests(prev =>
            prev.map(medicalLeaveRequest => {
                if (medicalLeaveRequest.id === id) medicalLeaveRequest.status = LeaveRequestStatus.APPROVED;
                return medicalLeaveRequest;
            })
        );
    };

    const updateMedicalLeaveRequestRejected = (id: string, rejectReason?: string) => {
        if (selectedStatus.value !== LeaveRequestStatus.REJECTED && selectedStatus.value !== 'ALL') {
            setMedicalLeaveRequests(prev => prev.filter(item => item.id !== id));
            return;
        }
        setMedicalLeaveRequests(prev =>
            prev.map(medicalLeaveRequest => {
                if (medicalLeaveRequest.id === id) {
                    medicalLeaveRequest.status = LeaveRequestStatus.REJECTED;
                    medicalLeaveRequest.rejectReason = rejectReason;
                }
                return medicalLeaveRequest;
            })
        );
    };

    const [page, setPage] = useState<number>(0);
    const [pageMedical, setPageMedical] = useState<number>(0);
    const [sizeMedical, setSizeMedical] = useState<number>(10);

    const [chosenRequestId, setChosenRequestId] = useState<string>('');
    const [isRejectVisible, setIsRejectVisibility] = useState<boolean>(false);
    const [isRejectDetailsVisible, setRejectDetailsVisibility] = useState<boolean>(false);
    const [rejectReason, setRejectReason] = useState<string | undefined>();

    const rejectLeaveRequest = (id: string, rejectReason?: string) => {
        const isMedical = medicalRequests.some(req => req.id === id);
        if (isMedical) {
            execRejectMedical(id, (returnedId: string) => updateMedicalLeaveRequestRejected(returnedId, rejectReason), rejectReason);
        } else {
            execReject(id, updateLeaveRequestRejected, rejectReason);
        }
    };

    const openRejectDetails = (reason: string) => {
        setRejectReason(reason);
        setRejectDetailsVisibility(true);
    };

    const openRejectRequest = (id: string) => {
        setChosenRequestId(id);
        setIsRejectVisibility(true);
    };

    const approveLeaveRequest = (id: string) => execApprove(id, updateLeaveRequestApproved);

    const { approveLeaveRequest: execApprove } = useHttpApproveLeaveRequest();
    const { rejectLeaveRequest: execReject } = useHttpRejectLeaveRequest();

    const { approveMedicalLeaveRequest: execApproveMedical } = useHttpApproveMedicalLeaveRequest();
    const { rejectMedicalLeaveRequest: execRejectMedical } = useHttpRejectMedicalLeaveRequest();

    const approveMedicalLeaveRequest = (id: string) => execApproveMedical(id, updateMedicalLeaveRequestApproved);

    const params: ILeaveRequestSearchParams = {
        status: selectedStatus.value === 'ALL' ? undefined : (selectedStatus.value as LeaveRequestStatus),
        size: perPage,
        page,
    };

    const { data: leaveRequests, setData: setLeaveRequests, total } = useHttpGetLeaveRequestsFromOrganizationOMX(organizationId, params);

    const paramsMedicalLeave: IMedicalLeaveRequestSearchParams = {
        status: selectedStatusMedical.value === 'ALL' ? undefined : (selectedStatusMedical.value as LeaveRequestStatus),
        size: sizeMedical,
        page: pageMedical,
    };
    const {
        data: medicalRequests,
        setData: setMedicalLeaveRequests,
        total: totalMedical,
    } = useHttpGetMedicalLeaveRequestsFromOrganization(paramsMedicalLeave);

    const mapMedicalToLeaveRequest = (medical: MedicalLeaveRequestUserInfoIncluded): LeaveRequestUserInfoIncluded => ({
        id: medical.id,
        userSub: medical.userSub,
        description: medical.description ?? undefined,
        status: medical.status,
        createdAt: new Date(medical.createdAt).toISOString(),
        fromDate: new Date(medical.startDate).toISOString(),
        toDate: medical.endDate ? new Date(medical.endDate).toISOString() : '/',
        policyType: LeaveRequestType.MEDICAL_LEAVE,
        leaveDurationType: LeaveDurationTypeEnum.FULL_DAY,
        email: medical.email,
        givenName: medical.givenName,
        familyName: medical.familyName,
        imageUrl: medical.imageUrl,
    });

    const medicalLeaveRequests = [...medicalRequests.map(req => mapMedicalToLeaveRequest(req))];

    return (
        <main className="leave-request-owner">
            <RequestList
                requests={leaveRequests}
                paginationProps={{ page: page, total, elementsPerPage: perPage, setElementsPerPage: setPerPage, setPage }}
                filterProps={{ selectedStatus, setSelectedStatus, filterItems }}
                approveRequest={approveLeaveRequest}
                rejectProps={{ openRejectRequest, openRejectDetails }}
                title={t('leaveRequest.requestList')}
            />
            <RequestList
                requests={medicalLeaveRequests}
                paginationProps={{
                    page: pageMedical,
                    total: totalMedical,
                    elementsPerPage: sizeMedical,
                    setElementsPerPage: setSizeMedical,
                    setPage: setPageMedical,
                }}
                filterProps={{ selectedStatus: selectedStatusMedical, setSelectedStatus: setSelectedStatusMedical, filterItems }}
                approveRequest={approveMedicalLeaveRequest}
                rejectProps={{ openRejectRequest, openRejectDetails }}
                title={t('medicalLeaveRequest.medicalLeaveRequests')}
            />

            <Modal isVisible={isRejectVisible} closeModal={() => setIsRejectVisibility(false)}>
                <RejectLeaveRequest id={chosenRequestId} rejectLeaveRequest={rejectLeaveRequest} close={() => setIsRejectVisibility(false)} />
            </Modal>

            <Modal isVisible={isRejectDetailsVisible} closeModal={() => setRejectDetailsVisibility(false)}>
                <MessageBox
                    title={t('leaveRequest.rejectionReason')}
                    description={rejectReason ?? ''}
                    close={() => setRejectDetailsVisibility(false)}
                />
            </Modal>
        </main>
    );
};
